import { useCallback } from 'react';
import { switchcase, useNoticeBannersStackContext } from '@sg/garnish';

import { useSendLoginToAuthMachine } from '@order/AuthMachine';
import type { LoginInput } from '@order/graphql';
import { useLoginMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

export const useSignIn = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  const [signInResponse, signInMutation] = useLoginMutation();
  const signInResponseData = signInResponse.data?.login;
  const sendLoginAction = useSendLoginToAuthMachine();

  const isSigningIn = signInResponse.fetching;

  // ────────────────────────────────────────────────────────────────────

  const signIn = useCallback(
    async (input: LoginInput) => {
      const response = await signInMutation({ input });
      const data = response?.data?.login;

      const isNotSuccessfullySignedIn = data?.__typename !== 'LoginSuccess';

      if (isNotSuccessfullySignedIn) {
        const systemError = getUrqlError(response?.error, data as never);
        const userError = switchcase({
          InvalidCredentials: t('login.credentials-error'),
          AccountLocked: t('login.account-locked-error'),
        })(t('general.error'))(data?.__typename ?? '');

        addNoticeBanner({ text: userError, palette: 'caution' });
        track('sign-in.failure', { userError, systemError });

        return data;
      }

      sendLoginAction(data?.csrf);

      return data;
    },
    [signInMutation, sendLoginAction, t, addNoticeBanner, track],
  );

  return { signInResponseData, isSigningIn, signIn };
};
